import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, 
  Minus, 
  Trash2, 
  ArrowLeft, 
  ShoppingCart,
  CheckCircle,
  Truck,
  Lock,
  X,
  ShoppingBag,
  User,
  Phone,
  MapPin,
  Edit2,
  Save,
  Package,
  Store
} from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, usersAPI } from '../services/api';

const Checkout = () => {
  const { cartItems, addToCart, removeFromCart, removeItemCompletely, updateQuantity, getTotalPrice, clearCart } = useCart();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [orderType, setOrderType] = useState('delivery'); // 'delivery' or 'takeaway'
  const shippingInfoRef = useRef(shippingInfo);
  
  // Keep ref in sync with state
  useEffect(() => {
    shippingInfoRef.current = shippingInfo;
  }, [shippingInfo]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load user shipping information
  useEffect(() => {
    if (user) {
      setShippingInfo({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  }, [user]);

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleShippingChange = useCallback((e) => {
    setShippingInfo(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handleSaveShipping = useCallback(async () => {
    const currentInfo = shippingInfoRef.current;
    
    // Validate required fields
    if (!currentInfo.name || !currentInfo.phone || !currentInfo.address) {
      alert('Please fill in all shipping information fields.');
      return;
    }

    // Validate phone number (10 digits)
    if (!/^\d{10}$/.test(currentInfo.phone)) {
      alert('Phone number must be exactly 10 digits.');
      return;
    }

    // Validate address length
    if (currentInfo.address.length < 10) {
      alert('Address must be at least 10 characters long.');
      return;
    }

    setIsSavingShipping(true);
    try {
      const response = await usersAPI.update(user.id, {
        name: currentInfo.name,
        phone: currentInfo.phone,
        address: currentInfo.address
      });
      
      updateUser(response.data);
      setIsEditingShipping(false);
      alert('Shipping information updated successfully!');
    } catch (error) {
      console.error('Error updating shipping info:', error);
      const errorMessage = error.response?.data?.errors || error.response?.data?.error || 'Failed to update shipping information. Please try again.';
      alert(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
    } finally {
      setIsSavingShipping(false);
    }
  }, [user, updateUser]);

  const handleCheckout = async () => {
    // Validate shipping information before checkout
    if (!shippingInfo.name || !shippingInfo.phone || !shippingInfo.address) {
      alert('Please complete your shipping information before placing the order.');
      setIsEditingShipping(true);
      return;
    }

    setIsProcessing(true);
    try {
      const response = await ordersAPI.create({
        order_type: orderType
      });
      
      if (response.status === 201) {
        const orderId = response.data.id;
        clearCart();
        setIsProcessing(false);
        navigate(`/order/pay/${orderId}`);
      } else {
        throw new Error('Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setIsProcessing(false);
      alert('Failed to place order. Please try again.');
    }
  };

  const subtotal = getTotalPrice();
  const shipping = orderType === 'takeaway' ? 0 : 50; 
  const total = subtotal + shipping;

  // Shipping Info Form JSX - MUST be defined before any conditional returns to follow Rules of Hooks
  // Not memoized to allow inputs to update properly while typing
  const renderShippingInfoForm = (
    <div className="mb-6 pb-6 border-b border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Truck className="text-green-600 text-lg" />
          <h3 className="text-lg font-bold text-gray-900">Shipping Information</h3>
        </div>
        {!isEditingShipping && (
          <button
            onClick={() => setIsEditingShipping(true)}
            className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1"
          >
            <Edit2 className="w-3 h-3" />
            <span>Edit</span>
          </button>
        )}
      </div>

      {isEditingShipping ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-3 h-3 mr-1 text-gray-500" />
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={shippingInfo.name}
              onChange={handleShippingChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter your full name"
              required
              autoComplete="name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Phone className="w-3 h-3 mr-1 text-gray-500" />
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={shippingInfo.phone}
              onChange={handleShippingChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="10 digit phone number"
              maxLength="10"
              required
              autoComplete="tel"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-3 h-3 mr-1 text-gray-500" />
              Delivery Address
            </label>
            <textarea
              name="address"
              value={shippingInfo.address}
              onChange={handleShippingChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              placeholder="Enter your complete delivery address"
              required
              autoComplete="street-address"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSaveShipping}
              disabled={isSavingShipping}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-full font-medium transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isSavingShipping ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
            <button
              onClick={() => {
                setIsEditingShipping(false);
                // Reset to original values
                setShippingInfo({
                  name: user?.name || '',
                  phone: user?.phone || '',
                  address: user?.address || ''
                });
              }}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full font-medium transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <User className="w-4 h-4 text-gray-400 mt-1" />
            <div>
              <p className="text-xs text-gray-500">Name</p>
              <p className="text-sm font-medium text-gray-900">{shippingInfo.name || 'Not provided'}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Phone className="w-4 h-4 text-gray-400 mt-1" />
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm font-medium text-gray-900">{shippingInfo.phone || 'Not provided'}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-gray-400 mt-1" />
            <div>
              <p className="text-xs text-gray-500">Address</p>
              <p className="text-sm font-medium text-gray-900">{shippingInfo.address || 'Not provided'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Empty cart state - moved after hooks
  if (cartItems.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 px-4 pt-8 sm:pt-12 pb-8 sm:pb-10">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center">
              <ShoppingCart className="text-4xl text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Your cart is empty</h2>
            <p className="text-gray-600 mb-6 text-lg">Add some amazing products to get started!</p>
            <button
              onClick={() => navigate('/dashboard')}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  const OrderSummaryBlock = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-gray-600 text-sm sm:text-base">Subtotal</span>
        <span className="font-semibold text-gray-900 text-sm sm:text-base">₹{subtotal.toFixed(2)}</span>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-gray-600 text-sm sm:text-base">Delivery Fee</span>
          <Truck className="text-green-600 text-xs" />
        </div>
        <span className="font-semibold text-green-600 text-sm sm:text-base">
          {shipping === 0 ? 'FREE' : `₹${shipping.toFixed(2)}`}
        </span>
      </div>
      <div className="border-t border-gray-200 pt-4 mt-2">
        <div className="flex justify-between items-center">
          <span className="text-lg font-bold text-gray-900">Total</span>
          <span className="text-2xl font-bold text-green-600">₹{total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-24 lg:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Shopping</span>
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
              <Lock className="w-3 h-3" />
              <span>Secure</span>
            </div>
          </div>
          <p className="text-gray-600 text-sm sm:text-base">Review your items and complete your order</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Cart Items Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 sm:px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="text-white text-lg" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900">Your Cart</h2>
                      <p className="text-xs sm:text-sm text-gray-600">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
                    </div>
                  </div>
                  <button
                    onClick={clearCart}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 px-3 rounded-full text-xs sm:text-sm font-medium transition-colors duration-200 whitespace-nowrap flex-shrink-0"
                    title="Clear all items from cart"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-2">
                {cartItems.map((item, index) => {
                  const product = item.product || item;
                  const productName = product.name || item.name;
                  const productImage = product.image_url || item.image_url;
                  const productPrice = product.price || item.price || 0;
                  const formattedPrice = product.formatted_price || item.formatted_price || `₹${productPrice.toFixed(2)}`;
                  const itemTotal = productPrice * item.quantity;

                  return (
                    <div 
                      key={item.id} 
                      className="group relative flex items-center gap-3 sm:gap-4 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 hover:border-green-300 transition-all duration-200"
                    >
                      {/* Product Image */}
                      <div className="relative flex-shrink-0">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                          <img
                            src={productImage}
                            alt={productName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                            }}
                          />
                        </div>
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0 flex items-center justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-0.5 group-hover:text-green-700 transition-colors duration-200 truncate">
                            {productName}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-500">
                              Qty: <span className="font-semibold text-gray-700">{item.quantity}</span>
                            </span>
                            <span className="text-xs text-gray-500">
                              @ {formattedPrice}
                            </span>
                          </div>
                          
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3 mt-2">
                            <div className="flex items-center gap-2 bg-white rounded-full p-1 border border-gray-200">
                              <button
                                onClick={() => handleQuantityChange(item.product_id || item.id, item.quantity - 1)}
                                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-all duration-200 shadow-sm"
                                aria-label="Decrease quantity"
                                title="Decrease quantity"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  handleQuantityChange(item.product_id || item.id, val);
                                }}
                                className="w-10 text-center border-0 bg-white font-semibold text-gray-900 text-xs focus:outline-none focus:ring-0 rounded-md"
                                aria-label="Item quantity"
                              />
                              
                              <button
                                onClick={() => addToCart(product)}
                                className="w-7 h-7 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-all duration-200 shadow-sm"
                                aria-label="Increase quantity"
                                title="Increase quantity"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>

                            <button
                              onClick={() => removeItemCompletely(item.product_id || item.id)}
                              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-all duration-200"
                              title="Remove item"
                              aria-label="Remove item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Total Price */}
                        <div className="text-right flex-shrink-0">
                          <p className="text-base sm:text-lg font-bold text-gray-900">
                            ₹{itemTotal.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Order Summary - Desktop */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 sticky top-8 border border-gray-100 hidden lg:block">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-white text-lg" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Order Summary</h2>
              </div>
              
              {/* Order Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Order Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setOrderType('delivery')}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 ${
                      orderType === 'delivery'
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                    }`}
                  >
                    <Truck className={`w-6 h-6 mb-1 ${orderType === 'delivery' ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="text-xs font-bold">Delivery</span>
                  </button>
                  <button
                    onClick={() => setOrderType('takeaway')}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 ${
                      orderType === 'takeaway'
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                    }`}
                  >
                    <Package className={`w-6 h-6 mb-1 ${orderType === 'takeaway' ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="text-xs font-bold">Take Away</span>
                  </button>
                </div>
              </div>

              {orderType === 'delivery' && renderShippingInfoForm}
              
              {orderType === 'takeaway' && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex gap-3">
                    <Store className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-blue-900">Pickup from Store</h4>
                      <p className="text-xs text-blue-700 mt-1">Please collect your order from our store during business hours.</p>
                      <p className="text-xs font-medium text-blue-800 mt-2">No delivery charges apply.</p>
                      <p className="text-xs font-medium text-blue-800 mt-2">We will notify you when your order is ready, please check notifications for your order updates.</p>
                    </div>
                  </div>
                </div>
              )}
              
              <OrderSummaryBlock />

              <button
                onClick={handleCheckout}
                disabled={isProcessing || cartItems.length === 0}
                className="w-full mt-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 text-white py-4 px-6 rounded-full font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:shadow-none flex items-center justify-center gap-2 transform hover:-translate-y-0.5 disabled:transform-none"
                aria-busy={isProcessing}
                aria-label="Place order"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Place Order</span>
                  </>
                )}
              </button>
              {/* Security Badge */}
              <div className="mt-6 pt-6 border-t border-gray-200 flex items-center gap-2 text-xs text-gray-500">
                <Lock className="w-3 h-3" />
                <span>Secure checkout with encrypted payment</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      {isMobile && cartItems.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 bg-white border-t-2 border-gray-200 shadow-2xl lg:hidden">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                <p className="text-2xl font-bold text-green-600">₹{total.toFixed(2)}</p>
              </div>
              <button
                onClick={() => setMobileSummaryOpen(true)}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-full font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                aria-label="View order summary"
              >
                <ShoppingBag className="w-4 h-4" />
                <span>Review & Pay</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Slide-up Summary Drawer */}
      {isMobile && mobileSummaryOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300" 
            onClick={() => setMobileSummaryOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto animate-slide-up">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900">Order Summary</h2>
              <button                 onClick={() => setMobileSummaryOpen(false)} 
                 className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                 aria-label="Close summary"
               >
                 <X className="w-4 h-4 text-gray-600" />
               </button>
            </div>
            
            <div className="px-6 py-6">
              {/* Order Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Order Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setOrderType('delivery')}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 ${
                      orderType === 'delivery'
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                    }`}
                  >
                    <Truck className={`w-6 h-6 mb-1 ${orderType === 'delivery' ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="text-xs font-bold">Delivery</span>
                  </button>
                  <button
                    onClick={() => setOrderType('takeaway')}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 ${
                      orderType === 'takeaway'
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                    }`}
                  >
                    <Package className={`w-6 h-6 mb-1 ${orderType === 'takeaway' ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="text-xs font-bold">Take Away</span>
                  </button>
                </div>
              </div>

              {orderType === 'delivery' && renderShippingInfoForm}
              
              {orderType === 'takeaway' && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex gap-3">
                    <Store className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-blue-900">Pickup from Store</h4>
                      <p className="text-xs text-blue-700 mt-1">Please collect your order from our store during business hours.</p>
                      <p className="text-xs font-medium text-blue-800 mt-2">No delivery charges apply.</p>
                      <p className="text-xs font-medium text-blue-800 mt-2">We will notify you when your order is ready, please check notifications for your order updates.</p>
                    </div>
                  </div>
                </div>
              )}
              
              <OrderSummaryBlock />
              
              <button
                onClick={() => {
                  setMobileSummaryOpen(false);
                  handleCheckout();
                }}
                disabled={isProcessing || cartItems.length === 0}
                className="w-full mt-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 text-white py-4 px-6 rounded-full font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:shadow-none flex items-center justify-center gap-2"
                aria-busy={isProcessing}
                aria-label="Place order"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Place Order</span>
                  </>
                )}
              </button>
              {/* Security Badge */}
              <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-center gap-2 text-xs text-gray-500">
                <Lock className="w-3 h-3" />
                <span>Secure checkout with encrypted payment</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add custom animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default Checkout;
